import React, {Children, forwardRef, useId, useMemo} from 'react';

import {Label} from '@pb/kit-component-label';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {CustomGroupItemContext} from './custom-group-item-context';

import styles from './custom-group.component.module.styl';

import type {ReactNode, ComponentProps, ForwardedRef} from 'react';

type NativeProps = ComponentProps<'div'>;

const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export interface CustomGroupProps extends NativeProps {
    /**
     * The children are passed to CustomGroup, often `<CustomGroupItem/>`
     */
    children: ReactNode,
}

export const CustomGroupItemType = {
    ERROR: 'ERROR',
    DEFAULT: 'DEFAULT',
} as const;

const TYPE_CLASSES = {
    [CustomGroupItemType.ERROR]: 'custom-group__row_error',
    [CustomGroupItemType.DEFAULT]: '',
};

export const MAX_WIDTH = 216;

/**
 * CustomGroup component is a container for CustomGroupItem components.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-input-custom-group--documentation}
 */
export const CustomGroup = (props: CustomGroupProps) => {
    const {children, ...nativeProps} = props;

    return (
        <div
            role="group"
            {...nativeProps}
            className={classNames('custom-group', nativeProps?.className)}
            style={{maxWidth: MAX_WIDTH, ...nativeProps?.style}}>
            {children}
        </div>
    );
};

CustomGroup.displayName = 'CustomGroup';

export interface CustomGroupItemProps extends NativeProps {
    /**
     * Type that defines input appearance either default or error
     */
    componentType?: keyof typeof CustomGroupItemType,
    /**
     * Input label, displayed before input
     */
    label?: ReactNode,
    /**
     * Props to pass to the `Label` component
     */
    labelProps?: ComponentProps<typeof Label>,
}

/**
 * CustomGroupItem component is a label and input pair.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-input-custom-group-custom-group-item--documentation}
 */
export const CustomGroupItem = forwardRef(({
    label,
    labelProps,
    componentType = CustomGroupItemType.DEFAULT,
    children,
    ...nativeProps
}: CustomGroupItemProps, ref: ForwardedRef<HTMLDivElement>) => {
    const generatedId = useId();
    const contextValue = useMemo(() => ({inputId: labelProps?.htmlFor || generatedId}),
        [generatedId, labelProps?.htmlFor]);

    const rootElement = Children.only(children);

    return (
        <div
            ref={ref}
            {...nativeProps}
            className={classNames('custom-group__row', TYPE_CLASSES?.[componentType], nativeProps?.className)}>
            <div className={classes['custom-group__cell']}>
                <Label
                    htmlFor={contextValue.inputId}
                    {...labelProps}
                    rootProps={{
                        ...labelProps?.rootProps,
                        className: classes['custom-group__label-wrapper'],
                    }}
                    className={classNames(classes['custom-group__label'], labelProps?.className)}>
                    {labelProps?.children || label}
                </Label>
            </div>
            <div className={classes['custom-group__cell']}>
                <CustomGroupItemContext.Provider value={contextValue}>
                    {rootElement}
                </CustomGroupItemContext.Provider>
            </div>
        </div>
    );
});

CustomGroupItem.displayName = 'CustomGroupItem';
